import { call, put, race, delay, select } from "redux-saga/effects";
import { resetApiError, riseError } from "store/features/general";
import {
    CreativeUploads,
    getBookingsReq,
    uploadThirdPartyInfoReq,
    MaterialUpload,
    uploadMaterialsReq,
    getPreflightReportReq,
    PreflightReport,
    removeMaterialReq,
    AdMaterial,
    PrintPreflightReport,
    getPrintPreflightReportReq,
    createThirdPartyInfoReq,
    MatSpecsNewThirdPartyMaterial,
    DeletedMaterialRes,
    updateThirdPartyTagReq,
    ThirdPartyUploadResponse,
    MaterialSpecification,
    updateMaterialSpecStatusReq,
    submitPrintMaterialDescriptionReq,
    submitPrintMaterialAdServerPropertiesReq,
    fetchMaterialSpecById,
    getAdMaterialReq,
} from "api/creatives";
import {
    fetchBookings,
    uploadMaterials,
    fetchThirdPartyTag,
    submitThirdPartyTag,
    submitNewCreativeThirdPartyTag,
    fetchValidationReport,
    removeMaterial,
    fetchPrintValidationReport,
    updateMaterialSpecStatus,
    setMaterialSpecWorkingStatus,
    getMaterialSpecWorkingStatus,
    submitPrintMaterialDescription,
    submitPrintMaterialAdServerProperties,
    getMaterialSpecById as getMaterialSpec,
    updateMaterialStatus,
    updateExistingSpecProperties,
} from "store/features/materialSpecs/specs";
import {
    buildCreativesViewModel,
    buildMaterials,
    buildMaterialSpecs,
    buildUploadedMaterialsViewModel,
    CreativesUploadViewModel,
    MaterialSpecificationViewModel,
} from "api/factories/creatives.factory";
import { TabTypes } from "pages/MaterialSpecs/components";

export function* updateExistingSpecPropertiesSaga(
    headers: HeadersInit,
    { payload: { materialSpecId } }: ReturnType<typeof updateExistingSpecProperties.request>
): Generator {
    try {
        const materialSpec = (yield call(fetchMaterialSpecById, headers, materialSpecId)) as MaterialSpecification;
        yield put(
            updateExistingSpecProperties.success({
                specId: materialSpecId,
                status: materialSpec.status,
                permissions: materialSpec.permissions,
                materials: buildMaterials(materialSpec.admaterial),
            })
        );
    } catch (err) {
        if (err.status) {
            yield put(updateExistingSpecProperties.failure({ statusCode: err.status }));
            yield put(resetApiError());

            return;
        }

        yield put(riseError(err));
    }
}

function* finalizeMaterialUpload(
    headers: HeadersInit,
    materialSpecId: number,
    materialId: number,
    selectedTab: TabTypes,
    creative: CreativesUploadViewModel,
    updateMaterialIds: (materialSpecId: number, materialId: number) => void
): Generator {
    yield put(uploadMaterials.success(creative));
    updateMaterialIds(materialSpecId, materialId);
    yield call(updateExistingSpecPropertiesSaga, headers, updateExistingSpecProperties.request({ materialSpecId }));

    const materialSpec = (yield select(getMaterialSpec(materialSpecId))) as MaterialSpecificationViewModel;

    if (materialSpec.isPrinted) {
        yield put(fetchPrintValidationReport.request(materialId));
    } else {
        yield put(fetchValidationReport.request(materialId));
    }
}

export function* fetchBookingsSaga(headers: HeadersInit): Generator {
    try {
        const campaign = (yield call(getBookingsReq, headers)) as CreativeUploads;
        yield put(fetchBookings.success(buildCreativesViewModel(campaign)));
    } catch (err) {
        if (err.status) {
            yield put(fetchBookings.failure({ statusCode: err.status }));
            yield put(resetApiError());

            return;
        }

        yield put(riseError(err));
    }
}

export function* fetchThirdPartyTagSaga(
    headers: HeadersInit,
    action: ReturnType<typeof fetchThirdPartyTag.request>
): Generator {
    try {
        const material = (yield call(getAdMaterialReq, headers, action.payload.adMaterialId)) as AdMaterial;

        yield put(
            fetchThirdPartyTag.success({
                materialSpecId: action.payload.currentMaterialSpecificationId,
                adMaterialId: action.payload.adMaterialId,
                tag: material?.tag,
                sgCode: material?.sgcode,
                description: material?.description || "",
                // TODO: props from bellow are missing in response
                // pixelTracker, url
            })
        );
    } catch (err) {
        if (err.status) {
            yield put(fetchThirdPartyTag.failure({ statusCode: err.status }));
            yield put(resetApiError());

            return;
        }

        yield put(riseError(err));
    }
}

export function* submitThirdPartyTagSaga(
    headers: HeadersInit,
    { payload }: ReturnType<typeof submitThirdPartyTag.request>
): Generator {
    try {
        yield call(uploadThirdPartyInfoReq, headers, payload);

        // updating tag results in updating the whole admaterial
        if (payload.tag || payload.sgCode) {
            const updatedMaterial = (yield call(updateThirdPartyTagReq, headers, {
                materialSpecificationId: payload.materialSpecificationId,
                adMaterialId: payload.adMaterialId,
                tag: payload.tag,
                sgCode: payload.sgCode,
            })) as ThirdPartyUploadResponse;
            const updatedMaterialFullInfo: MaterialUpload = {
                ...updatedMaterial,
                matspecid: payload.materialSpecificationId,
            };

            yield put(uploadMaterials.success(buildUploadedMaterialsViewModel([updatedMaterialFullInfo])));
            yield call(
                updateExistingSpecPropertiesSaga,
                headers,
                updateExistingSpecProperties.request({ materialSpecId: payload.materialSpecificationId })
            );
        }

        //tag itself isn't included in the above model so it still needs another call to update it...
        yield put(submitThirdPartyTag.success(payload));
    } catch (err) {
        if (err.status) {
            yield put(
                submitThirdPartyTag.failure({
                    statusCode: err.status,
                    message: err.parsedError.error,
                    materialSpecificationId: payload.materialSpecificationId,
                    adMaterialId: payload.adMaterialId,
                })
            );
            yield put(resetApiError());

            return;
        }

        yield put(riseError(err));
    }
}

export function* submitNewCreativeThirdPartyTagSaga(
    headers: HeadersInit,
    { payload }: ReturnType<typeof submitNewCreativeThirdPartyTag.request>
): Generator {
    try {
        const thirdPartyAdMaterialResponse = (yield call(
            createThirdPartyInfoReq,
            headers,
            payload
        )) as MaterialUpload[];
        const createdAdMaterialId = thirdPartyAdMaterialResponse[0].admaterial.id;
        const matSpecsNewAdMaterial = {
            ...buildUploadedMaterialsViewModel(thirdPartyAdMaterialResponse),
            currentMaterialSpecification: payload.materialSpecificationId,
            adMaterialId: createdAdMaterialId,
        } as MatSpecsNewThirdPartyMaterial;

        yield put(submitNewCreativeThirdPartyTag.success(matSpecsNewAdMaterial));
        yield call(
            updateExistingSpecPropertiesSaga,
            headers,
            updateExistingSpecProperties.request({ materialSpecId: payload.materialSpecificationId })
        );
    } catch (err) {
        if (err.status) {
            yield put(
                submitThirdPartyTag.failure({
                    statusCode: err.status,
                    message: err.parsedError.error,
                    materialSpecificationId: payload.materialSpecificationId,
                })
            );
            yield put(resetApiError());

            return;
        }

        yield put(riseError(err));
    }
}

export function* fetchValidationReportSaga(
    headers: HeadersInit,
    { payload }: ReturnType<typeof fetchValidationReport.request>
): Generator {
    try {
        const preflightReport = (yield call(getPreflightReportReq, headers, payload)) as PreflightReport;
        yield put(fetchValidationReport.success({ materialId: payload, preflightReport }));
    } catch (err) {
        if (err.status) {
            yield put(fetchValidationReport.failure({ statusCode: err.status }));
            yield put(resetApiError());

            return;
        }

        yield put(riseError(err));
    }
}

export function* fetchPrintValidationReportSaga(
    headers: HeadersInit,
    { payload }: ReturnType<typeof fetchPrintValidationReport.request>
): Generator {
    try {
        const preflightReport = (yield call(getPrintPreflightReportReq, headers, payload)) as PrintPreflightReport;
        yield put(fetchPrintValidationReport.success({ materialId: payload, preflightReport }));
    } catch (err) {
        if (err.status) {
            yield put(fetchPrintValidationReport.failure({ statusCode: err.status }));
            yield put(resetApiError());

            return;
        }

        yield put(riseError(err));
    }
}

export function* removeMaterialSaga(
    headers: HeadersInit,
    { payload: { materialId, materialSpecId, updateMaterialIds } }: ReturnType<typeof removeMaterial.request>
): Generator {
    try {
        const material = (yield call(removeMaterialReq, headers, materialId)) as DeletedMaterialRes;

        // update material id when it was deleted and new material is returned
        if (material.admaterial) {
            updateMaterialIds(materialSpecId, material.admaterial?.id);
        }

        yield put(
            removeMaterial.success({
                materials: material.admaterial && !material.deleted ? buildMaterials([material.admaterial]) : [],
                materialSpecId,
                deletedMaterialId: materialId,
            })
        );
        yield call(
            updateExistingSpecPropertiesSaga,
            headers,
            updateExistingSpecProperties.request({ materialSpecId: materialSpecId })
        );
    } catch (err) {
        if (err.status) {
            yield put(removeMaterial.failure({ statusCode: err.status }));
            yield put(resetApiError());

            return;
        }

        yield put(riseError(err));
    }
}

// TODO: Remove race when server events/WS will be implemented
export function* updateMaterialSpecStatusSaga(
    headers: HeadersInit,
    { payload: { action, isStatusUpdated, ...params } }: ReturnType<typeof updateMaterialSpecStatus.request>
): Generator {
    try {
        const isWorking = (yield select(getMaterialSpecWorkingStatus(params.materialSpecId))) as boolean;

        // if material spec has no working status or is set for the first time
        if (!isWorking && isStatusUpdated === undefined) {
            const materialSpec = (yield call(
                updateMaterialSpecStatusReq,
                headers,
                action,
                params
            )) as MaterialSpecification;
            const [materialSpecViewMode] = buildMaterialSpecs([materialSpec]);
            yield put(setMaterialSpecWorkingStatus({ materialSpec: materialSpecViewMode }));

            // call again saga
            return yield put(
                updateMaterialSpecStatus.request({
                    action,
                    isStatusUpdated: params.status === materialSpec.status.code,
                    ...params,
                })
            );
        }

        // if material spec has working status se
        const { spec } = (yield race({
            spec: yield call(fetchMaterialSpecById, headers, params.materialSpecId),
            timeout: yield delay(1000),
        })) as { spec: MaterialSpecification; timeout: number };

        // and if server still says material is working, retry saga again
        if (spec.status.code === 3) {
            return yield put(updateMaterialSpecStatus.request({ action, ...params }));
        }

        const [materialSpecViewMode] = buildMaterialSpecs([spec]);

        yield put(updateMaterialSpecStatus.success({ materialSpec: materialSpecViewMode }));
    } catch (err) {
        if (err.status) {
            yield put(updateMaterialSpecStatus.failure({ statusCode: err.status, message: JSON.stringify(err) }));
            yield put(resetApiError());

            return;
        }

        yield put(riseError(err));
    }
}

export function* submitPrintMaterialDescriptionSaga(
    headers: HeadersInit,
    { payload }: ReturnType<typeof submitPrintMaterialDescription.request>
): Generator {
    try {
        yield call(submitPrintMaterialDescriptionReq, headers, payload);

        yield put(submitPrintMaterialDescription.success(payload));
    } catch (err) {
        if (err.status) {
            yield put(submitPrintMaterialDescription.failure({ statusCode: err.status }));
            yield put(resetApiError());

            return;
        }

        yield put(riseError(err));
    }
}

export function* submitPrintMaterialAdServerPropertiesSaga(
    headers: HeadersInit,
    { payload }: ReturnType<typeof submitPrintMaterialAdServerProperties.request>
): Generator {
    try {
        yield call(submitPrintMaterialAdServerPropertiesReq, headers, payload);

        yield put(submitPrintMaterialAdServerProperties.success(payload));
    } catch (err) {
        if (err.status) {
            yield put(submitPrintMaterialAdServerProperties.failure({ statusCode: err.status }));
            yield put(resetApiError());

            return;
        }

        yield put(riseError(err));
    }
}

export function* uploadMaterialsSaga(
    headers: HeadersInit,
    action: ReturnType<typeof uploadMaterials.request>,
    retries = 0,
    isWorking = false
): Generator {
    const { materialId, materialSpecId, updateMaterialIds, files, selectedTab } = action.payload;
    const formData = new FormData();
    formData.append("matspec", materialSpecId.toString());

    // should be use only to update material
    if (materialId) {
        formData.append("admaterial", materialId.toString());
    }

    formData.append("thumbnailsize", "100");

    files.forEach((file) => {
        formData.append("files[]", file);
    });

    try {
        if (!isWorking && retries === 0) {
            const uploadedMaterial = (yield call(uploadMaterialsReq, headers, formData)) as MaterialUpload[];
            const newlyUploadedMaterial = buildUploadedMaterialsViewModel(uploadedMaterial);
            const [material] = newlyUploadedMaterial.materials;

            // check current status
            if (material.status.code === 3) {
                return yield race({
                    adMaterial: yield call(
                        uploadMaterialsSaga,
                        headers,
                        { ...action, payload: { ...action.payload, materialId: material.id } },
                        retries + 1
                    ),
                    timeout: yield delay(1000),
                });
            }

            return yield call(
                finalizeMaterialUpload,
                headers,
                materialSpecId,
                material.id,
                selectedTab,
                newlyUploadedMaterial,
                updateMaterialIds
            );
        }

        // after first uplaod attempt this id MUST be always available, both for optional and existing material
        if (materialId) {
            const { adMaterial } = (yield race({
                adMaterial: yield call(getAdMaterialReq, headers, materialId),
                timeout: yield delay(1000 * 1.2 * retries),
            })) as { adMaterial: AdMaterial; timeout: number };

            // if it's still "working" then retry again
            if (adMaterial.status.code === 3) {
                yield put(updateMaterialStatus({ materialId, status: adMaterial.status }));

                return yield call(uploadMaterialsSaga, headers, action, retries + 1, true);
            }

            return yield call(
                finalizeMaterialUpload,
                headers,
                materialSpecId,
                adMaterial.id,
                selectedTab,
                buildUploadedMaterialsViewModel([{ admaterial: adMaterial, matspecid: materialSpecId }]),
                updateMaterialIds
            );
        }
    } catch (err) {
        if (err.status) {
            yield put(uploadMaterials.failure({ statusCode: err.status }));
            yield put(resetApiError());

            return;
        }

        yield put(riseError(err));
    }
}
